<template>
  <div class="cmd-palette">

    <!-- กล่อง trigger บน navbar -->
    <div class="cmd-trigger" :class="{ 'cmd-trigger--dark': isDark }" title="ค้นหาเมนู (กด /)" @click="open">
      <feather-icon icon="SearchIcon" svgClasses="h-4 w-4" />
    </div>

    <!-- modal palette -->
    <transition name="cmd-fade">
      <div v-if="isOpen" class="cmd-overlay" @mousedown.self="close">
        <div class="cmd-modal" :class="{ 'cmd-modal--dark': isDark }">

          <!-- ช่องค้นหา -->
          <div class="cmd-search">
            <feather-icon icon="SearchIcon" svgClasses="h-5 w-5 cmd-search__icon" />
            <input
              ref="input"
              v-model="query"
              type="text"
              class="cmd-search__input"
              placeholder="ค้นหาเมนู หรือพิมพ์ @username แล้วกด Enter..."
              @keydown.down.prevent="move(1)"
              @keydown.up.prevent="move(-1)"
              @keydown.enter.prevent="selectActive"
              @keydown.esc.prevent="close" />
            <span class="cmd-search__kbd" @click="close">ESC</span>
          </div>

          <!-- ผลลัพธ์ -->
          <div ref="list" class="cmd-results">

            <!-- โหมดเปิดยูส (@username) -->
            <div
              v-if="userMode"
              class="cmd-row cmd-row--active"
              @click="openUser">
              <feather-icon icon="UserIcon" svgClasses="h-4 w-4 cmd-row__icon" />
              <span class="cmd-row__name">เปิดข้อมูลยูส: <strong>{{ usernameInput || '...' }}</strong></span>
            </div>

            <!-- ค้นหา/แสดงเมนู -->
            <template v-else>
              <template v-for="(grp, gi) in groups">
                <div v-if="grp.items.length" :key="'h' + gi" class="cmd-group-label">{{ grp.label }}</div>
                <div
                  v-for="item in grp.items"
                  :key="grp.label + '|' + item.url"
                  class="cmd-row"
                  :class="{ 'cmd-row--active': item._idx === activeIndex }"
                  @mousemove="activeIndex = item._idx"
                  @click="go(item)">
                  <feather-icon :icon="item.icon || 'ChevronRightIcon'" svgClasses="h-4 w-4 cmd-row__icon" />
                  <span class="cmd-row__name">{{ item.name }}</span>
                  <span v-if="item.group" class="cmd-row__group">{{ item.group }}</span>
                  <feather-icon
                    icon="StarIcon"
                    :svgClasses="['h-4 w-4 cmd-star', isPinned(item.url) ? 'cmd-star--on' : '']"
                    @click.native.stop="togglePin(item.url)" />
                </div>
              </template>
              <div v-if="!flatVisible.length" class="cmd-empty">ไม่พบเมนูที่ตรงกับ “{{ query }}”</div>
            </template>
          </div>

          <!-- footer hint -->
          <div class="cmd-footer">
            <span><span class="cmd-footer__kbd">↑</span><span class="cmd-footer__kbd">↓</span> เลื่อน</span>
            <span><span class="cmd-footer__kbd">↵</span> เลือก</span>
            <span><span class="cmd-footer__kbd">★</span> ปักหมุด</span>
            <span><span class="cmd-footer__kbd">esc</span> ปิด</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const PINNED_KEY = 'commandPalettePinned'
const RECENT_KEY = 'commandPaletteRecent'
const RECENT_MAX = 8

export default {
  name: 'command-palette',
  props: {
    navMenuItems: { type: Array, default: () => [] }
  },
  data () {
    return {
      isOpen: false,
      query: '',
      activeIndex: 0,
      pinned: [],
      recent: []
    }
  },
  computed: {
    isDark () {
      return this.$store.state.theme === 'dark'
    },
    // เมนูทั้งหมดที่ flatten เป็นแถวค้นหา (เฉพาะที่มี url)
    flatItems () {
      const out = []
      ;(this.navMenuItems || []).forEach(item => {
        if (Array.isArray(item.submenu)) {
          item.submenu.forEach(sub => {
            if (sub.url) out.push({ name: sub.name, url: sub.url, group: item.name, icon: item.icon, target: sub.target || item.target })
          })
        } else if (item.url) {
          out.push({ name: item.name, url: item.url, group: '', icon: item.icon, target: item.target })
        }
      })
      return out
    },
    byUrl () {
      const map = {}
      this.flatItems.forEach(i => { map[i.url] = i })
      return map
    },
    userMode () {
      return this.query.trim().startsWith('@')
    },
    usernameInput () {
      return this.query.trim().replace(/^@/, '').trim()
    },
    // จัดกลุ่มผลลัพธ์ พร้อมใส่ _idx ต่อเนื่องสำหรับเลื่อนด้วยคีย์บอร์ด
    groups () {
      let idx = 0
      const tag = items => items.map(i => ({ ...i, _idx: idx++ }))
      const q = this.query.trim().toLowerCase()

      if (q) {
        const matches = this.flatItems.filter(i => i.name.toLowerCase().includes(q))
        return [{ label: '', items: tag(matches) }]
      }

      const pinnedItems = this.pinned.map(u => this.byUrl[u]).filter(Boolean)
      const pinnedSet = new Set(this.pinned)
      const recentItems = this.recent.map(u => this.byUrl[u]).filter(Boolean).filter(i => !pinnedSet.has(i.url))
      const allItems = this.flatItems.filter(i => !pinnedSet.has(i.url))

      const result = []
      if (pinnedItems.length) result.push({ label: 'ปักหมุด', items: tag(pinnedItems) })
      if (recentItems.length) result.push({ label: 'ล่าสุด', items: tag(recentItems) })
      result.push({ label: 'เมนูทั้งหมด', items: tag(allItems) })
      return result
    },
    flatVisible () {
      return this.groups.reduce((acc, g) => acc.concat(g.items), [])
    }
  },
  watch: {
    query () {
      this.activeIndex = 0
    }
  },
  created () {
    this.pinned = this.loadList(PINNED_KEY)
    this.recent = this.loadList(RECENT_KEY)
  },
  mounted () {
    document.addEventListener('keydown', this.onGlobalKey)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onGlobalKey)
  },
  methods: {
    loadList (key) {
      try {
        const v = JSON.parse(localStorage.getItem(key))
        return Array.isArray(v) ? v : []
      } catch (e) { return [] }
    },
    saveList (key, list) {
      try { localStorage.setItem(key, JSON.stringify(list)) } catch (e) { /* noop */ }
    },
    onGlobalKey (e) {
      if (this.isOpen) return
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const el = e.target
      const tag = el && el.tagName ? el.tagName.toLowerCase() : ''
      if (tag === 'input' || tag === 'textarea' || (el && el.isContentEditable)) return
      e.preventDefault()
      this.open()
    },
    open () {
      this.isOpen = true
      this.query = ''
      this.activeIndex = 0
      this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus() })
    },
    close () {
      this.isOpen = false
    },
    move (dir) {
      const n = this.flatVisible.length
      if (!n) return
      this.activeIndex = (this.activeIndex + dir + n) % n
      this.$nextTick(() => {
        if (!this.$refs.list) return
        const active = this.$refs.list.querySelector('.cmd-row--active')
        if (active) active.scrollIntoView({ block: 'nearest' })
      })
    },
    selectActive () {
      if (this.userMode) { this.openUser(); return }
      const item = this.flatVisible[this.activeIndex]
      if (item) this.go(item)
    },
    go (item) {
      this.pushRecent(item.url)
      this.close()
      if (item.target === '_blank') {
        const href = this.$router.resolve(item.url).href
        window.open(href, '_blank')
        return
      }
      if (this.$route.path !== item.url) this.$router.push(item.url).catch(() => {})
    },
    openUser () {
      const u = this.usernameInput
      if (!u) return
      this.close()
      this.$router.push(`/member/${u}`).catch(() => {})
    },
    pushRecent (url) {
      const list = [url, ...this.recent.filter(u => u !== url)].slice(0, RECENT_MAX)
      this.recent = list
      this.saveList(RECENT_KEY, list)
    },
    isPinned (url) {
      return this.pinned.includes(url)
    },
    togglePin (url) {
      this.pinned = this.isPinned(url) ? this.pinned.filter(u => u !== url) : [...this.pinned, url]
      this.saveList(PINNED_KEY, this.pinned)
    }
  }
}
</script>

<style>
/* คลาสทั้งหมด prefix ด้วย cmd- จึงไม่ scoped เพื่อให้ style ทะลุถึง <svg> ใน feather-icon ได้ */
/* ===== trigger บน navbar ===== */
.cmd-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  margin-right: 10px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #6b7280;
  cursor: pointer;
  transition: border-color .15s, background .15s;
}
.cmd-trigger:hover { border-color: rgba(0, 0, 0, 0.18); }
.cmd-trigger--dark {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  color: #b6bee3;
}
.cmd-trigger--dark:hover { border-color: rgba(255, 255, 255, 0.28); }

/* ===== overlay + modal ===== */
.cmd-overlay {
  position: fixed; inset: 0; z-index: 60000;
  background: rgba(17, 22, 58, 0.45);
  display: flex; align-items: flex-start; justify-content: center;
  padding-top: 12vh;
}
.cmd-modal {
  width: 92%; max-width: 640px;
  background: #fff; color: #1f2937;
  border-radius: 14px; overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
  display: flex; flex-direction: column;
  font-family: inherit;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
}
.cmd-modal--dark { background: #232a52; color: #e6e9f5; }

/* search box */
.cmd-search {
  display: flex; align-items: center; gap: 10px;
  margin: 14px; padding: 11px 14px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: border-color .15s;
}
.cmd-search:focus-within { border-color: rgba(115, 103, 240, 0.6); }
.cmd-modal--dark .cmd-search { background: rgba(255, 255, 255, 0.04); border-color: rgba(255, 255, 255, 0.14); }
.cmd-modal--dark .cmd-search:focus-within { background: rgba(255, 255, 255, 0.07); border-color: rgba(115, 103, 240, 0.7); }
.cmd-search__icon { opacity: .55; flex: 0 0 auto; }
/* .cmd-modal นำหน้าเพื่อให้ชนะ .theme-dark input ของธีม ที่ยัดพื้นหลังทึบให้ input ทุกตัว */
.cmd-modal .cmd-search__input {
  flex: 1; border: none; outline: none; background: transparent;
  font-family: inherit; font-size: 15px; font-weight: 400; color: inherit;
  caret-color: currentColor; box-shadow: none;
}
.cmd-modal .cmd-search__input::placeholder { color: #9ca3af !important; font-weight: 400; }
.cmd-modal--dark .cmd-search__input::placeholder { color: #8b93bd !important; }
.cmd-search__kbd {
  font-size: 11px; padding: 2px 7px; border-radius: 5px; cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.18); opacity: .55; flex: 0 0 auto;
}
.cmd-modal--dark .cmd-search__kbd { border-color: rgba(255, 255, 255, 0.25); }

/* results */
.cmd-results { max-height: 52vh; overflow-y: auto; padding: 4px 8px 8px; }
.cmd-group-label {
  font-size: 11px; font-weight: 500; letter-spacing: .03em;
  color: #9ca3af; padding: 10px 12px 5px;
}
.cmd-row {
  display: flex; align-items: center; gap: 12px;
  padding: 9px 12px; margin: 1px 0; border-radius: 8px;
  cursor: pointer; font-size: 14px;
}
.cmd-row__icon { opacity: .65; flex: 0 0 auto; }
.cmd-row__name { flex: 1; font-weight: 400; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cmd-row--active .cmd-row__name { font-weight: 500; }
.cmd-row__group { font-size: 12px; font-weight: 400; color: #9ca3af; flex: 0 0 auto; }
.cmd-row--active { background: rgba(115, 103, 240, 0.12); }
.cmd-modal--dark .cmd-row--active { background: rgba(115, 103, 240, 0.28); }
.cmd-star { opacity: 0; transition: opacity .12s; flex: 0 0 auto; }
.cmd-row:hover .cmd-star, .cmd-row--active .cmd-star { opacity: .45; }
.cmd-star:hover { opacity: 1 !important; }
.cmd-star--on { opacity: 1 !important; color: #f1b44c; fill: #f1b44c; }
.cmd-empty { padding: 24px 18px; text-align: center; color: #9ca3af; font-size: 14px; }

/* footer */
.cmd-footer {
  display: flex; gap: 18px; align-items: center;
  padding: 10px 18px; font-size: 12px; color: #9ca3af;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.cmd-modal--dark .cmd-footer { border-top-color: rgba(255, 255, 255, 0.08); }
.cmd-footer__kbd {
  display: inline-block; min-width: 16px; text-align: center; margin-right: 3px;
  padding: 1px 4px; border-radius: 4px; border: 1px solid currentColor; opacity: .7;
}

/* transition */
.cmd-fade-enter-active, .cmd-fade-leave-active { transition: opacity .15s; }
.cmd-fade-enter, .cmd-fade-leave-to { opacity: 0; }
</style>
